/**
 * Detects if the user is on a mobile device
 * @returns {boolean} true if the user is on a mobile device
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.innerWidth <= 820; // 820px is the breakpoint for mobile devices
};

/**
 * A page can take over phone clicks that would otherwise go to sign-up first.
 * Used by the phone widget studio (components/mobileWidgetStudio): on a widget
 * page that has one, "Create a Countdown" opens the studio instead. Returns
 * true when it handled the click. Unset everywhere else, so nothing changes.
 */
type TMobileRedirectTakeover = (redirectPath: string) => boolean;
let mobileRedirectTakeover: TMobileRedirectTakeover | null = null;

export const setMobileRedirectTakeover = (fn: TMobileRedirectTakeover | null) => {
  mobileRedirectTakeover = fn;
};

/**
 * Redirects mobile users to signup page, otherwise allows normal flow
 * @param {string} redirectPath - The path to redirect to after signup (default: '/signup')
 * @returns {boolean} true if redirection occurred, false if normal flow should continue
 */
export const handleMobileRedirect = (
  e: any,
  redirectPath: string = "/signup"
): boolean | undefined => {
  if (isMobileDevice()) {
    if (mobileRedirectTakeover && mobileRedirectTakeover(redirectPath)) {
      e.preventDefault();
      return true;
    }
    const signupUrl =
      !redirectPath || redirectPath === "/signup"
        ? "/signup"
        : `/signup?redirectUrl=${encodeURIComponent(redirectPath)}`;
    e.preventDefault();
    window.location.href = signupUrl;
    return true;
  }
};

/**
 * Creates a click handler that redirects mobile users to signup page
 * @param {Function} originalHandler - The original click handler, called on every device
 * @param {string} redirectPath - The path to redirect to after signup (default: '/signup')
 * @returns {Function} A click handler function
 */
export const createMobileAwareClickHandler = (
  originalHandler?: () => void,
  redirectPath: string = "/signup"
) => {
  return (e: any) => {
    // The handler runs FIRST, on every device. Every one of the ~92 call sites
    // passes an analytics-only handler, and handleMobileRedirect navigates away
    // on mobile, so anything after it never ran there. That silently made five
    // CTA events desktop-only, including the homepage and widget hub heroes.
    // See DEV-15194.
    if (originalHandler) {
      originalHandler();
    }

    handleMobileRedirect(e, redirectPath);
  };
};
